import React from 'react'
import PropTypes from 'prop-types'
import DayPickerInput from 'components/DayPickerInput'
import moment from 'moment'
import 'styles/d3/datepicker.scss'
import { dayPickerConfig } from 'utils/time'

class DatePicker extends React.Component {
  constructor(props) {
    super(props)
    const { initialDate } = props
    this.state = {
      valid: true,
      initialValue: initialDate ? (typeof initialDate.toDate === 'function' ? initialDate.toDate() : initialDate) : '',
    }
    this.inputRef = React.createRef()
  }

  handleBlur = e => {
    const { valid } = this.state
    const { initialDate, onInputBlur } = this.props
    if (valid) {
      this.setState({
        initialValue: initialDate ? initialDate.toDate() : '',
      })
    }
    onInputBlur?.(e)
  }

  removeError = () => this.inputRef.current?.classList.remove('has-error')

  onContainerClick = () => {
    const { onContainerClick } = this.props
    if (onContainerClick) onContainerClick()
    else this.inputRef?.current?.focus()
  }

  componentDidMount() {
    const { setRef, _ref } = this.props
    if (setRef && !_ref?.current) {
      setRef(this.inputRef)
    }
  }

  componentDidUpdate(prevProps) {
    const { setRef, _ref } = this.props
    if (setRef && _ref !== prevProps._ref && !_ref?.current) {
      setRef(this.inputRef)
    }
  }

  render() {
    const {
      date,
      disabled,
      disabledDays,
      errorText,
      hasError,
      helpText,
      id,
      inputClassName,
      onDayChange,
      onChangeRaw,
      onInputBlur,
      placeholder = dayPickerConfig.placeholder,
      overlayComponent,
      setRef,
      _ref,
      deferParseUntilFullYear = true,
      preserveInputWhileFocused = true,
    } = this.props
    const { valid, initialValue } = this.state

    return (
      <div className='day-picker-wrapper' onClick={this.onContainerClick}>
        <DayPickerInput
          inputProps={{
            id,
            disabled,
            className: `${inputClassName || ''} ${valid ? '' : 'invalid-date'}${hasError ? ' has-error' : ''}`,
            onBlur: this.handleBlur,
            ref: this.inputRef,
          }}
          dayPickerProps={{
            disabled: disabledDays,
            locale: dayPickerConfig.locale,
          }}
          format={dayPickerConfig.formatDateFns}
          onChangeRaw={onChangeRaw}
          onDayChange={(_date, _, options) => {
            if (_date && !valid) this.setState({ valid: true })
            else if (!_date && valid) this.setState({ valid: false })
            if (_date) onDayChange(moment(_date), options)
            else onDayChange(_date, options)
          }}
          value={date ? (typeof date?.toDate === 'function' ? date.toDate() : date) : initialValue}
          placeholder={placeholder}
          overlayComponent={overlayComponent}
          deferParseUntilFullYear={deferParseUntilFullYear}
          preserveInputWhileFocused={preserveInputWhileFocused}
        />
        <i className={`far fa-calendar-alt${hasError ? ' has-error' : ''}`} />
        {hasError && errorText && <p className='error-text'>{errorText}</p>}
        {!hasError && helpText && <p className='help-text'>{helpText}</p>}
      </div>
    )
  }
}

DatePicker.propTypes = {
  date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  disabled: PropTypes.bool,
  disabledDays: PropTypes.shape({
    after: PropTypes.instanceOf(Date),
    before: PropTypes.instanceOf(Date),
  }),
  errorText: PropTypes.string,
  hasError: PropTypes.bool,
  helpText: PropTypes.string,
  id: PropTypes.string,
  inputClassName: PropTypes.string,
  initialDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onDayChange: PropTypes.func,
  /** Fires on manual keystrokes in the text field (react-datepicker onChangeRaw equivalent). */
  onChangeRaw: PropTypes.func,
  onInputBlur: PropTypes.func,
  deferParseUntilFullYear: PropTypes.bool,
  preserveInputWhileFocused: PropTypes.bool,
  onContainerClick: PropTypes.func,
  setRef: PropTypes.func,
  _ref: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
}

export default DatePicker
